import { useMutation, useQueryClient } from '@tanstack/react-query';

import { renameWorkflow } from '@/lib/api/generated/api';
import type { WorkflowView } from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';
import { WorkflowRejectedError } from '../errors';

/** The bound the service enforces; mirrored so the field can reject early. */
export const WORKFLOW_NAME_MAX_LENGTH = 200;

/**
 * Renames a workflow. The display name is the only thing that changes — the
 * key, the definition, and every sibling are untouched (FR-031).
 *
 * The service trims and bounds the name and answers a bad one with the same
 * violation payload as import, so the field renders a rejection the same way
 * the upload dialog does. Success invalidates the workspace aggregate, which
 * is what updates the list, this header, and the boards list at once, without
 * a reload (FR-033).
 */
export function useRenameWorkflow(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workflowId,
      name,
    }: {
      workflowId: string;
      name: string;
    }): Promise<WorkflowView> => {
      const response = await renameWorkflow(workspaceId, workflowId, { name });
      if (response.status === 200) return response.data;
      if (response.status === 422) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      if (response.status === 404) {
        throw new Error('This workflow no longer exists in this workspace.');
      }
      throw new Error('The workflow could not be renamed.');
    },
    // Awaited, not fired and forgotten: the mutation stays pending until the
    // fresh aggregate has arrived, so the field closes onto the new name
    // instead of flashing the old one while the refetch is in flight.
    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
